import React, { useState, useEffect } from 'react';
import { Layout, Button, Menu, Drawer, Dropdown, Space, message } from 'antd';
import { PhoneOutlined, MenuOutlined, ToolOutlined, CarOutlined, PictureOutlined, MailOutlined, AppstoreOutlined, CalendarOutlined, UserOutlined, LogoutOutlined, DashboardOutlined, DownOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { shopConfig, getThemeColors } from '../../config/shop.config';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';

const { Header, Content } = Layout;

interface PublicLayoutProps {
  children: React.ReactNode;
}

/**
 * PublicLayout - Professional layout for customer-facing landing page
 * Includes navigation header with logo and call-to-action
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const location = useLocation();
  const themeColors = getThemeColors();
  const logo = shopConfig.branding?.logo;
  const { isAuthenticated, customerData, logout } = useCustomerAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  type NavItem = {
    key: string;
    label: string;
    icon: React.ReactNode;
    route?: string;
    id?: string;
  };

  const handleNavClick = (item: NavItem) => {
    if (item.route) {
      // Navigate to route
      navigate(item.route);
      setMobileMenuOpen(false);
    } else if (item.id) {
      // Scroll to section (only if on landing page)
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(item.id!);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        const element = document.getElementById(item.id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      setMobileMenuOpen(false);
    }
  };

  const handleCall = () => {
    window.location.href = `tel:${shopConfig.phone}`;
  };

  const handleLogout = async () => {
    try {
      await logout();
      message.success('Logged out successfully');
      navigate('/');
      setMobileMenuOpen(false);
    } catch (error) {
      message.error('Failed to logout');
    }
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems: NavItem[] = [
    { key: 'book', label: 'Book Service', icon: <CalendarOutlined />, route: '/book' },
    { key: 'services-info', label: 'Our Services', icon: <ToolOutlined />, id: 'services' },
    { key: 'gallery', label: 'Our Work', icon: <PictureOutlined />, id: 'gallery' },
    { key: 'contact', label: 'Contact', icon: <MailOutlined />, id: 'contact' },
  ];

  // Responsive breakpoints
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {/* Professional Navigation Header */}
      <Header
        style={{
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 1000,
          padding: isMobile ? '0 16px' : '0 24px',
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
          transition: 'all 0.3s ease',
          height: isMobile ? '64px' : '70px',
          lineHeight: isMobile ? '64px' : '70px',
          borderBottom: scrolled ? `2px solid ${themeColors.primary}` : '1px solid #e5e7eb',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap', height: '100%' }}>
          {/* Logo and Shop Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px', cursor: 'pointer', flex: '0 0 auto', height: '100%' }} onClick={handleLogoClick}>
            {logo ? (
              <img
                src={logo}
                alt={shopConfig.name}
                style={{
                  height: isMobile ? '40px' : '50px',
                  maxWidth: isMobile ? '140px' : '200px',
                  objectFit: 'contain',
                  mixBlendMode: 'darken',
                  filter: 'contrast(1.1)',
                }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                <div
                  style={{
                    width: isMobile ? '36px' : '45px',
                    height: isMobile ? '36px' : '45px',
                    background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.accent} 100%)`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ToolOutlined style={{ fontSize: isMobile ? '18px' : '24px', color: 'white' }} />
                </div>
                {!isMobile && (
                  <div style={{ lineHeight: 1.2 }}>
                    <div style={{ fontSize: isTablet ? '16px' : '20px', fontWeight: 700, color: '#111827' }}>
                      {shopConfig.name}
                    </div>
                    <div style={{ fontSize: '12px', color: themeColors.primary, fontWeight: 600 }}>
                      Automotive Excellence
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop & Tablet Navigation */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: isTablet ? '12px' : '24px', flex: '1 1 auto', justifyContent: 'flex-end', overflow: 'hidden' }}>
              {/* Navigation Links - hide on small tablets if needed */}
              {isDesktop && navItems.map((item) => (
                <a
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  style={{
                    color: '#4b5563',
                    fontSize: '15px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'color 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = themeColors.primary)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#4b5563')}
                >
                  {item.icon}
                  {item.label}
                </a>
              ))}

              {/* Tablet - show hamburger menu for navigation items, keep auth buttons */}
              {isTablet && (
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={() => setMobileMenuOpen(true)}
                  style={{ color: themeColors.primary, fontSize: '18px' }}
                />
              )}

              {isAuthenticated ? (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'dashboard',
                        label: 'My Dashboard',
                        icon: <DashboardOutlined />,
                        onClick: () => navigate('/customer/dashboard'),
                      },
                      {
                        key: 'divider',
                        type: 'divider',
                      },
                      {
                        key: 'logout',
                        label: 'Logout',
                        icon: <LogoutOutlined />,
                        onClick: handleLogout,
                        danger: true,
                      },
                    ],
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    type="default"
                    size={isTablet ? 'middle' : 'large'}
                    style={{
                      borderColor: themeColors.primary,
                      color: themeColors.primary,
                      fontWeight: 600,
                      height: isTablet ? '40px' : '45px',
                      borderRadius: '8px',
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                    }}
                  >
                    <Space size={4}>
                      <UserOutlined />
                      {!isTablet && (customerData?.customer?.first_name || 'Account')}
                      <DownOutlined style={{ fontSize: '12px' }} />
                    </Space>
                  </Button>
                </Dropdown>
              ) : (
                <Button
                  type="default"
                  size={isTablet ? 'middle' : 'large'}
                  icon={<UserOutlined />}
                  onClick={() => navigate('/customer/login')}
                  style={{
                    borderColor: themeColors.primary,
                    color: themeColors.primary,
                    fontWeight: 600,
                    height: isTablet ? '40px' : '45px',
                    borderRadius: '8px',
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                  }}
                >
                  {isTablet ? 'Login' : 'Customer Login'}
                </Button>
              )}

              <Button
                type="primary"
                size={isTablet ? 'middle' : 'large'}
                icon={<PhoneOutlined />}
                onClick={handleCall}
                style={{
                  backgroundColor: themeColors.primary,
                  borderColor: themeColors.primary,
                  fontWeight: 600,
                  height: isTablet ? '40px' : '45px',
                  borderRadius: '8px',
                  boxShadow: `0 4px 12px ${themeColors.primary}40`,
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                }}
              >
                {isTablet ? 'Call' : shopConfig.phone}
              </Button>
            </div>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Button
              type="text"
              size="large"
              icon={<MenuOutlined style={{ fontSize: '24px' }} />}
              onClick={() => setMobileMenuOpen(true)}
              style={{ color: themeColors.primary }}
            />
          )}
        </div>
      </Header>

      {/* Mobile & Tablet Drawer Menu */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.accent} 100%)`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ToolOutlined style={{ fontSize: '20px', color: 'white' }} />
            </div>
            <span style={{ fontSize: '18px', fontWeight: 600 }}>{shopConfig.name}</span>
          </div>
        }
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={isMobile ? 280 : 320}
        styles={{ body: { padding: '24px 16px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {navItems.map((item) => (
            <Button
              key={item.key}
              type="text"
              size="large"
              icon={item.icon}
              onClick={() => handleNavClick(item)}
              style={{
                justifyContent: 'flex-start',
                height: '52px',
                fontSize: '16px',
                fontWeight: 500,
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${themeColors.primary}10`;
                e.currentTarget.style.color = themeColors.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '';
              }}
            >
              {item.label}
            </Button>
          ))}

          <div style={{ height: '1px', background: '#e5e7eb', margin: '16px 0' }} />

          {isAuthenticated ? (
            <>
              <div style={{
                padding: '12px',
                background: `${themeColors.primary}10`,
                borderRadius: '8px',
                border: `1px solid ${themeColors.primary}30`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <UserOutlined style={{ color: themeColors.primary, fontSize: '16px' }} />
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    {customerData?.customer?.first_name || 'Customer'}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginLeft: '24px' }}>
                  {customerData?.customer?.email}
                </div>
              </div>
              <Button
                type="default"
                size="large"
                icon={<DashboardOutlined />}
                onClick={() => {
                  navigate('/customer/dashboard');
                  setMobileMenuOpen(false);
                }}
                style={{
                  justifyContent: 'center',
                  height: '52px',
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  borderColor: themeColors.primary,
                  color: themeColors.primary,
                }}
              >
                My Dashboard
              </Button>
              <Button
                type="default"
                size="large"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                danger
                style={{
                  justifyContent: 'center',
                  height: '52px',
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: '8px',
                }}
              >
                Logout
              </Button>
            </>
          ) : (
            <Button
              type="default"
              size="large"
              icon={<UserOutlined />}
              onClick={() => {
                navigate('/customer/login');
                setMobileMenuOpen(false);
              }}
              style={{
                justifyContent: 'center',
                height: '52px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '8px',
                borderColor: themeColors.primary,
                color: themeColors.primary,
              }}
            >
              Customer Login
            </Button>
          )}

          <Button
            type="primary"
            size="large"
            icon={<PhoneOutlined />}
            onClick={handleCall}
            style={{
              backgroundColor: themeColors.primary,
              height: '52px',
              fontSize: '16px',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: `0 4px 12px ${themeColors.primary}40`,
            }}
          >
            Call {shopConfig.phone}
          </Button>
        </div>
      </Drawer>

      {/* Content with top padding to account for fixed header */}
      <Content style={{ padding: 0, marginTop: isMobile ? '64px' : '70px' }}>
        {children}
      </Content>
    </Layout>
  );
};

export default PublicLayout;
